import type { RichTextDocument } from "./rich-text-document.js";
import { parseSoapDraftMarkdown } from "./soap-draft-markdown.js";

/**
 * Converts a machine-generated SOAP Draft (the generation adapter's Markdown
 * text) into the `RichTextDocument` shape the Draft Version column stores and
 * the NO-413 editor mounts.
 *
 * The input is untrusted model output, so this is deliberately NOT a general
 * Markdown parser. `shared/soap-draft-markdown.ts` recognises a closed subset
 * (ATX headings clamped to levels 1-3, bullet and ordered lists with one
 * nesting level, one-level blockquotes, `---`, and `**bold**`) and constructs
 * only the node and mark types it enumerates. It never builds a link mark, an
 * image node, or an HTML node, and it sets only the closed attributes
 * `heading.level` and `orderedList.start`. Links become their text, images
 * their alt text, and everything else — bare URLs, raw HTML, code, tables,
 * italics — stays literal characters, so model output cannot smuggle a link
 * or any structure the grammar does not name into a stored Draft.
 *
 * NO-479 consumes stored documents (read + autosave); it never re-derives one
 * from text, so this helper is the only writer of machine Draft content, and
 * Versions written before NO-526 keep their verbatim-paragraph shape.
 */
export function soapDraftDocumentFromText(text: string): RichTextDocument {
  return parseSoapDraftMarkdown(text);
}

/**
 * The inverse view: the plain text of a stored rich-text document, used when a
 * persisted document (today the Appointment Note pinned into an Enhancement
 * snapshot) becomes model evidence. Block nodes separate with a blank line and
 * hard breaks with a newline; marks, attributes, and node types carry no text
 * and are ignored, so no editor structure leaks into a prompt.
 */
export function plainTextFromRichTextDocument(document: unknown): string {
  return blockTexts(document)
    .map((block) => block.replace(/[ \t]+$/gm, "").trim())
    .filter((block) => block.length > 0)
    .join("\n\n");
}

/**
 * Serializes the persisted SOAP Draft for the clipboard as Markdown. Unlike the
 * model evidence projection above, this preserves the editor's block structure
 * so headings (`#` prefixes, level clamped to 1-3), list markers (`- `, `N. `),
 * nested indentation, blockquotes, and intentional empty blocks survive the
 * copy, and it wraps each bold run in `**`. Only the bold mark is serialized;
 * every other mark contributes its text alone, and attributes beyond heading
 * level and ordered start are presentation or metadata that never enter the
 * text. Nothing is ever backslash-escaped: an RD-typed `*`, `_`, `#`, `[`,
 * backtick, or `~` reaches the clipboard verbatim.
 */
export function cleanSoapTextFromRichTextDocument(draftDocument: unknown): string {
  const root = richTextRecord(draftDocument);
  if (root?.type !== "doc") return "";

  return structuredChildren(root.content, "\n\n");
}

interface RichTextRecord {
  type?: unknown;
  text?: unknown;
  attrs?: unknown;
  marks?: unknown;
  content?: unknown;
}

function richTextRecord(node: unknown): RichTextRecord | null {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;

  return node as RichTextRecord;
}

function structuredChildren(content: unknown, separator: string): string {
  if (!Array.isArray(content)) return "";

  return content.map(structuredText).join(separator);
}

function structuredText(node: unknown): string {
  const record = richTextRecord(node);
  if (!record) return "";

  if (record.type === "text") return typeof record.text === "string" ? record.text : "";

  if (record.type === "hardBreak") return "\n";

  switch (record.type) {
    case "doc":
      return structuredChildren(record.content, "\n\n");
    case "paragraph":
      return inlineMarkdown(record.content);
    case "heading":
      return `${"#".repeat(headingLevel(record.attrs))} ${inlineMarkdown(record.content)}`;
    case "codeBlock":
      return structuredChildren(record.content, "");
    case "bulletList":
      return structuredList(record, false);
    case "orderedList":
      return structuredList(record, true);
    case "listItem":
      return structuredChildren(record.content, "\n");
    case "blockquote":
      return structuredChildren(record.content, "\n\n")
        .split("\n")
        .map((line) => (line.length > 0 ? `> ${line}` : ">"))
        .join("\n");
    case "horizontalRule":
      return "---";
    default:
      return structuredChildren(record.content, "");
  }
}

/**
 * Inline content as Markdown: contiguous bold-marked text coalesces into one
 * `**` run, a run ends at a hardBreak so no delimiter straddles a line, and
 * the run's leading and trailing whitespace stays outside the delimiters
 * (`** text **` is not emphasis). No character is escaped.
 */
function inlineMarkdown(content: unknown): string {
  if (!Array.isArray(content)) return "";

  let output = "";
  let boldRun = "";

  for (const node of content) {
    const record = richTextRecord(node);

    if (record?.type === "text" && hasBoldMark(record.marks)) {
      boldRun += typeof record.text === "string" ? record.text : "";
      continue;
    }

    output += boldMarkdown(boldRun);
    boldRun = "";
    output += structuredText(node);
  }

  return output + boldMarkdown(boldRun);
}

function boldMarkdown(run: string): string {
  const [, leading, core, trailing] = /^(\s*)([\s\S]*?)(\s*)$/.exec(run)!;

  // A run with no visible text emits its whitespace and no delimiters.
  return core.length > 0 ? `${leading}**${core}**${trailing}` : run;
}

function hasBoldMark(marks: unknown): boolean {
  return (
    Array.isArray(marks) && marks.some((mark) => richTextRecord(mark)?.type === "bold")
  );
}

/** Level 1-3; a missing, non-integer, or out-of-range level clamps rather than throws. */
function headingLevel(attrs: unknown): number {
  if (!attrs || typeof attrs !== "object" || Array.isArray(attrs)) return 1;

  const { level } = attrs as { level?: unknown };
  if (typeof level !== "number" || !Number.isInteger(level)) return 1;

  return Math.min(Math.max(level, 1), 3);
}

function structuredList(list: RichTextRecord, ordered: boolean): string {
  if (!Array.isArray(list.content)) return "";

  const start = orderedListStart(list.attrs);

  return list.content
    .map((item, index) => {
      const prefix = ordered ? `${start + index}. ` : "- ";
      const body = structuredText(item);
      if (body.length === 0) return prefix.trimEnd();

      const indentation = " ".repeat(prefix.length);
      const indentedBody = body
        .split("\n")
        .map((line, lineIndex) =>
          lineIndex === 0 || line.length === 0 ? line : `${indentation}${line}`
        )
        .join("\n");

      return `${prefix}${indentedBody}`;
    })
    .join("\n");
}

function orderedListStart(attrs: unknown): number {
  if (!attrs || typeof attrs !== "object" || Array.isArray(attrs)) return 1;

  const { start } = attrs as { start?: unknown };

  return typeof start === "number" && Number.isInteger(start) ? start : 1;
}

function blockTexts(node: unknown): string[] {
  if (Array.isArray(node)) return node.flatMap(blockTexts);

  if (!node || typeof node !== "object") return [];

  const record = node as {
    type?: unknown;
    text?: unknown;
    content?: unknown;
  };
  if (record.type === "doc") return blockTexts(record.content);

  return [inlineText(node)];
}

function inlineText(node: unknown): string {
  if (Array.isArray(node)) return node.map(inlineText).join("");

  if (!node || typeof node !== "object") return "";

  const record = node as {
    type?: unknown;
    text?: unknown;
    content?: unknown;
  };

  if (record.type === "text") {
    return typeof record.text === "string" ? record.text : "";
  }

  if (record.type === "hardBreak") return "\n";

  const inner = record.content === undefined ? "" : inlineText(record.content);

  // Nested blocks (list items, blockquote children) keep their own line.
  return record.type === "listItem" || record.type === "paragraph"
    ? `${inner}\n`
    : inner;
}
